import { useState, useEffect } from "react";
import { X, Sparkles, Copy, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import { SUMMER_SALE, saleLabels } from "@/lib/promo";

const PROMO_CODE = SUMMER_SALE.code;
const PROMO_END = new Date("2026-09-30T23:59:59Z").getTime();
const DISMISS_KEY = "summer_sale_banner_dismissed_v1";

const labels: Record<string, { msg: string; cta: string }> = {
  fr: { msg: saleLabels.fr.banner + " · code", cta: "Copier" },
  en: { msg: saleLabels.en.banner + " · code", cta: "Copy" },
  nl: { msg: saleLabels.nl.banner + " · code", cta: "Kopiëren" },
  pl: { msg: saleLabels.pl.banner + " · kod", cta: "Kopiuj" },
  ro: { msg: saleLabels.ro.banner + " · cod", cta: "Copiază" },
};

export const LaunchPromoBanner = () => {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [lang, setLang] = useState<string>("fr");

  useEffect(() => {
    if (!SUMMER_SALE.active || Date.now() > PROMO_END) return;
    if (sessionStorage.getItem(DISMISS_KEY) === "1") return;
    setVisible(true);
    try {
      const raw = localStorage.getItem("i18n-storage");
      if (raw) {
        const parsed = JSON.parse(raw);
        const l = parsed?.state?.language;
        if (l && labels[l]) setLang(l);
      }
    } catch {}
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(PROMO_CODE);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  const { msg, cta } = labels[lang] ?? labels.en;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-[60] overflow-hidden bg-gradient-to-r from-primary/90 via-primary to-primary/90 text-primary-foreground"
        >
          <div className="container mx-auto flex items-center justify-center gap-3 px-4 py-2 text-xs sm:text-sm">
            <Sparkles className="h-4 w-4 shrink-0" aria-hidden />
            <span className="text-center">
              {msg}{" "}
              <button
                onClick={copy}
                className="inline-flex items-center gap-1 rounded-md border border-primary-foreground/30 bg-primary-foreground/10 px-2 py-0.5 font-mono font-semibold tracking-wider transition hover:bg-primary-foreground/20"
              >
                {PROMO_CODE}
                {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                <span className="sr-only">{cta}</span>
              </button>
            </span>
            <button
              onClick={dismiss}
              aria-label="Dismiss"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 transition hover:bg-primary-foreground/10"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};